import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {LoadingButton} from "@mui/lab";
import {
    Alert,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import * as React from "react";
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from "i18nano";
import {ApiService} from "../api/ApiService";
import {useNotification} from "../stores/notificationStore";
import WithLogoAndSelectLanguageWrapper from "../components/WithLogoAndSelectLanguageWrapper";
import StackCard from "../components/StackCard";

interface ActivatePageProps {
    title: string;
}

const ActivatePage: React.FC<ActivatePageProps> = ({title}) => {
    const navigate = useNavigate();
    const ts = useTranslation();

    const [loading, setLoading] = useState(false)
    const [checkToken, setCheckToken] = useState(true);

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [t, setT] = useState<string | null>(null);
    const [u, setU] = useState<string | null>(null);

    const handleChangePassword = (e: any) => setPassword(e.target.value);
    const handleChangeConfirmPassword = (e: any) => setConfirmPassword(e.target.value);

    const [errors, setErrors] = useState<string[]>([]);
    const {pathname, search} = useLocation();
    const notification = useNotification();

    const submitHandler = async () => {
        if (password !== confirmPassword) {
            setErrors(['Password mismatch'])
            return;
        }
        if (!t || !u) return
        setLoading(true)
        const response = await ApiService.SetPassword(password, confirmPassword, t, u);
        if (response.success) {
            notification.open("Done!", 'success');
            navigate("/login");
        } else {
            setErrors([response.errors[0]]);
        }
        setLoading(false)
    }

    const getRawParam = (queryString: string, paramName: string) => {
        const regex = new RegExp(`[?&]${paramName}=([^&]*)`);
        const match = queryString.match(regex);
        return match ? match[1] : null;
    };

    useEffect(() => {
        const tParams = getRawParam(search, 't');
        const uParams = getRawParam(search, 'u');
        if (!tParams || !uParams) return;
        setT(tParams);
        setU(uParams);
    }, [])

    useEffect(() => {
        localStorage.removeItem("lastApplicant");
        if (!t || !u) {
            setCheckToken(false);
            setLoading(false)
            return;
        }
        if (pathname !== "/activate") {
            setCheckToken(false);
            setLoading(false)
            return;
        }
        ApiService.CheckActivated(u).then(response => {
            if (response.success && response.data) navigate("/login");
            setCheckToken(false);
            setLoading(false)
        });
    }, [t, u]);


    return (
            <WithLogoAndSelectLanguageWrapper>
                {checkToken ?
                        <CircularProgress sx={{position: "absolute", left: "calc(50% - 20px)", top: "30%"}}/>
                        : <StackCard>
                            {
                                !u || !t
                                        ?
                                        <Typography variant="h5" align={'center'}> Not valid link </Typography>
                                        :
                                        (<>
                                            <Typography variant="h4" align={'center'}>
                                                {title}
                                            </Typography>
                                            <Typography align={"justify"} variant={"body2"}>
                                                {ts("Choose a password that you will easily remember")}
                                            </Typography>
                                            <TextField
                                                    disabled={false}
                                                    fullWidth
                                                    id="password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={handleChangePassword}
                                                    onKeyPress={e => {
                                                        if (e.code === "Enter") {
                                                            if (password === "" || confirmPassword === "") return;
                                                            submitHandler();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                                <InputAdornment position="end"
                                                                                onClick={() => setShowPassword(prev => !prev)}
                                                                                onMouseDown={e => e.preventDefault()}
                                                                >
                                                                    <IconButton
                                                                            onMouseDown={e => e.preventDefault()}
                                                                            edge="end">
                                                                        {showPassword ? <VisibilityOff/> :
                                                                                <Visibility/>}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                        ),
                                                    }
                                                    }
                                                    aria-describedby="outlined-email-helper-text"
                                                    label={ts("New password")}
                                                    variant="outlined"
                                            />
                                            <TextField
                                                    disabled={false}
                                                    fullWidth
                                                    id="confirmPassword"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={handleChangeConfirmPassword}
                                                    onKeyPress={e => {
                                                        if (e.code === "Enter") {
                                                            if (password === "" || confirmPassword === "") return;
                                                            submitHandler();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                                <InputAdornment position="end"
                                                                                onClick={() => setShowPassword(prev => !prev)}
                                                                                onMouseDown={e => e.preventDefault()}
                                                                >
                                                                    <IconButton
                                                                            onMouseDown={e => e.preventDefault()}
                                                                            edge="end">
                                                                        {showPassword ? <VisibilityOff/> :
                                                                                <Visibility/>}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                        ),
                                                    }
                                                    }
                                                    aria-describedby="outlined-email-helper-text"
                                                    label={ts("Confirm new password")}
                                                    variant="outlined"
                                            />

                                            {errors?.map(e => <Alert severity="error">{e}</Alert>)}
                                            <LoadingButton disabled={password === "" || confirmPassword === ""}
                                                           loadingPosition="start"
                                                           startIcon={<></>} loading={loading} size={"large"}
                                                           variant="contained"
                                                           onClick={submitHandler}>
                                                {pathname === "/restore" ?
                                                        loading ? `${ts("Resetting")}...` : ts("Reset password") :
                                                        loading ? `${ts("Activating")}...` : ts("Activate")}
                                            </LoadingButton>
                                        </>)
                            }
                        </StackCard>
                }
            </WithLogoAndSelectLanguageWrapper>
    )
}

export default ActivatePage;